import JSZip from "jszip";
import { IPstRow } from "./xlsxHelper";
import { ITimestamp } from "../../components/TimestampsSelector/timestamps";

interface ITransformerData {
    node1: string,
    node2: string,
    orderCode: string,
    elementName: string,
    timestamp: number,
}

interface IRegulationData {
    node1: string,
    node2: string,
    orderCode: string,
    initialCgm: string,
    timestamp: number,
}

export interface IInitialCgm {
    actionId: string,
    timestamp: number,
    value?: number
}

const getLortData = (fileContent: string, lort: string) => {
    const transformerSplit = fileContent.split(lort);
    const remainingNetworkParts = transformerSplit[1].split("##");
    const neededLines = remainingNetworkParts[0].split("\n").filter(row => row.length !== 1);
    return neededLines;
};

const getNodesFromLine = (line: string) => {
    const node1 = line.substring(0, 8);
    const node2 = line.substring(9, 17);
    return { node1, node2 };
}

const mapToTransformerData = (data: string[], nodes: string[], timestamp: number) => {
    const filteredData = data.filter(line => {
        const { node1, node2 } = getNodesFromLine(line);
        if (nodes.includes(node1.trim()) || nodes.includes(node2.trim())) return true;
        if (nodes.some(x => node1.includes(x) || node2.includes(x))) return true;
        return false;
    });
    const mappedData = filteredData.map(line => {
        const { node1, node2 } = getNodesFromLine(line);
        const transformerData: ITransformerData = {
            node1: node1,
            node2: node2,
            orderCode: line.charAt(18),
            elementName: line.substring(77, 89),
            timestamp: timestamp
        };
        return transformerData;
    });
    return mappedData;
};

const mapToRegulationData = (data: string[], nodes: string[], timestamp: number) => {
    const filteredData = data.filter(line => {
        const { node1, node2 } = getNodesFromLine(line);
        if (nodes.includes(node1.trim()) || nodes.includes(node2.trim())) return true;
        if (nodes.some(x => node1.includes(x) || node2.includes(x))) return true;
        return false;
    });
    const mappedData = filteredData.map(line => {
        const { node1, node2 } = getNodesFromLine(line);
        const regulationData: IRegulationData = {
            node1: node1,
            node2: node2,
            orderCode: line.charAt(18),
            timestamp: timestamp,
            initialCgm: line.substring(54, 57),
        }
        return regulationData;
    });
    return mappedData;
};

const expandPstFromAsterisk = (pst: IPstRow, transformerData: ITransformerData[]) => {
    const from = pst.nodeFrom.substring(0, 7);
    const to = pst.nodeTo.substring(0, 7);
    const orderCode = pst.orderCode.toString();

    let transformers: ITransformerData[] = [];
    if (pst.nodeFrom.endsWith("*") && !pst.nodeTo.endsWith("*")) {
        transformers = transformerData.filter(t => {
            if (t.orderCode.trim() !== orderCode && t.elementName.trim() !== orderCode) return false;

            if ((t.node1.includes(from) && t.node2.trim() === pst.nodeFrom) || (t.node2.includes(from) && t.node1.trim() === pst.nodeTo)) return true;
            return false;
        });
    } else if (!pst.nodeFrom.endsWith("*") && pst.nodeTo.endsWith("*")) {
        transformers = transformerData.filter(t => {
            if (t.orderCode.trim() !== orderCode && t.elementName.trim() !== orderCode) return false;

            if ((t.node2.includes(to) && t.node1.trim() === pst.nodeFrom) || (t.node1.includes(to) && t.node2.trim() === pst.nodeFrom)) return true;
            return false;
        });
    } else if (pst.nodeFrom.endsWith("*") && pst.nodeTo.endsWith("*")) {
        transformers = transformerData.filter(t => {
            if (t.orderCode.trim() !== orderCode && t.elementName.trim() !== orderCode) return false;

            if (t.node1.includes(from) && t.node2.includes(to)) return true;
            if (t.node2.includes(from) && t.node1.includes(to)) return true;
            return false;
        });
    }

    const expandedPsts = transformers.map((x, idx) => {
        const row: IPstRow = { ...pst };
        row.raID = `${row.raID}_H${idx + 1}`;
        row.nodeFrom = x.node1;
        row.nodeTo = x.node2;
        row.orderCode = x.orderCode;
        return row;
    });
    return expandedPsts;
};

const getInitalCgm = (pst: IPstRow, timestamp: number, transformerData: ITransformerData[], regulationData: IRegulationData[]) => {
    let initialCgm: IInitialCgm = {
        actionId: pst.raID,
        timestamp: timestamp
    };
    const orderCode = pst.orderCode.toString();
    const transformer = transformerData.find(t => {
        if (t.orderCode.trim() !== orderCode && t.elementName.trim() !== orderCode) return false;
        if ((t.node1.trim() === pst.nodeFrom && t.node2.trim() === pst.nodeTo) || (t.node1.trim() === pst.nodeTo && t.node2.trim() === pst.nodeFrom)) return true;
        return false;
    });
    if (transformer === undefined) return initialCgm;

    const regulation = regulationData.find(r => {
        if (r.orderCode === transformer.orderCode && r.node1 === transformer.node1 && r.node2 === transformer.node2) return true;
        return false;
    });
    if (regulation === undefined) return initialCgm;

    initialCgm.value = parseInt(regulation.initialCgm);
    return initialCgm;
};

export const mapInitialCgmToRow = (tableRows: IPstRow[], initialCgms: IInitialCgm[], timestamp: number) => {
    const filteredCgms = initialCgms.filter(x => x.timestamp === timestamp);
    if (filteredCgms.length === 0) return tableRows;

    tableRows.forEach(x => {
        const initialCgm = filteredCgms.find(cgm => cgm.actionId === x.raID);
        x.initialCGM = initialCgm?.value;
    });
    return tableRows;
};

export const parseInitialCgmFromZip = async (zipFile: File, tableRows: IPstRow[] | undefined, activeTimestamps: ITimestamp[]) => {
    if (!tableRows) return;

    const zip = await JSZip.loadAsync(zipFile);
    const cgmDirectoryNames = Object.keys(zip.files)
        .filter(f => f.endsWith(".zip") && (f.includes("F100") || f.includes("F109") || f.includes("F308") || f.includes("F620") ||
            f.includes("-620-")
            || f.includes("LT-001") || f.includes("LT-002")
        ))
        .sort((a, b) => {
            if (a > b) return 1;
            if (b > a) return -1;
            return 0;
        });
    if (cgmDirectoryNames.length === 0) return;
    const cgmDirectoryName = cgmDirectoryNames.pop();

    const cgmFileZipData = await zip.file(cgmDirectoryName!)?.async("arraybuffer");
    const cgmFileZip = await JSZip.loadAsync(cgmFileZipData!);
    if (cgmFileZip === undefined) return;

    let initialCgms: IInitialCgm[] = [];
    const nodes = tableRows.map(x => {
        if (x.nodeFrom.endsWith("*")) return x.nodeFrom.substring(0, 7);
        return x.nodeFrom;
    }).concat(tableRows.map(x => {
        if (x.nodeTo.endsWith("*")) return x.nodeTo.substring(0, 7);
        return x.nodeTo;
    }));
    //for (let timestamp = 0; timestamp < 24; timestamp++) {
    for (const activeTimestamp of activeTimestamps) {
        const timestamp = activeTimestamp.value;
        const strTimestamp = activeTimestamp.cgmtimestamp;
        //const strTimestamp = String(timestamp).padStart(2, "0") + "30";
        const filenameToAnalyze = Object.keys(cgmFileZip.files).find(file => file.includes(strTimestamp));
        if (filenameToAnalyze === undefined) continue;

        const fileStringContent = await cgmFileZip.file(filenameToAnalyze!)?.async("string");
        const transformerData = mapToTransformerData(getLortData(fileStringContent!, "##T"), nodes, timestamp);
        const regulationData = mapToRegulationData(getLortData(fileStringContent!, "##R"), nodes, timestamp);

        const expandedPsts: IPstRow[] = tableRows
            .filter(x => x.nodeFrom.endsWith("*") || x.nodeTo.endsWith("*"))
            .map(x => expandPstFromAsterisk(x, transformerData))
            .flat()
            .filter(x => !tableRows.some(y => x.nodeFrom === y.nodeFrom && x.nodeTo === y.nodeTo));
        tableRows.push(...expandedPsts);

        const initialCgmsForTimestamp = tableRows.filter(x => x.timestamp === timestamp).map(row => getInitalCgm(row, timestamp, transformerData, regulationData));
        initialCgms.push(...initialCgmsForTimestamp);
    }
    return initialCgms;
};